import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "@/assets/logo.png";
import Button from "../buttons/Button";
import "./header.css";
import SideMenu from "./SideMenu";
import { AnimatePresence } from "framer-motion";

export type Menu = {
  name: string;
  label?: string;
  subItems?: string[];
};

const Header = () => {
  const [activeMenu, setActiveMenu] = useState<string>("home");
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>("");
  
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setVideoIsPlaying(document.body.classList.contains("video-playing"));
    });
  
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  
    return () => observer.disconnect();
  }, []);
  
  const navigate = useNavigate();
  const location = useLocation();

  let hideTimeout: NodeJS.Timeout;

  // Define which nav items are active
  const activeNavItems = ["summerdeals", "home", "servicii", "portfoliu", "SMM-content"]; // home and servicii are active
  const activeSubItems = ["branding", "online marketing", "web design", "e-commerce", "serverside tracking"]; // All servicii active

  const isNavItemActive = (itemName: string) => activeNavItems.includes(itemName);
  const isSubItemActive = (subItem: string) => activeSubItems.includes(subItem);

  const handleMouseEnter = (name: string) => {
    clearTimeout(hideTimeout);
    // Only show dropdown for active items
    if (isNavItemActive(name)) {
      setHoveredMenu(name);
    }
  };

  const handleMouseLeave = () => {
    hideTimeout = setTimeout(() => {
      setHoveredMenu(null);
    }, 200);
  };

  const menuItems: Menu[] = [
    { name: "summerdeals", label: "SummerDeals" },
    { name: "home" },
    {
      name: "servicii",
      subItems: ["branding", "online marketing", "web design", "e-commerce", "serverside tracking"],
    },
    { name: "portfoliu" },
//    { name: "online marketing HOT pack" }, // Changed from "contact" to "SMM-Content"
    { name: "SMM-content" },
  ];

  const handleMenuClick = (menu: string): void => {
    // Only allow clicks on active nav items
    if (!isNavItemActive(menu)) return;

    setBurgerOpen(false);
    setActiveMenu(menu);

    // Handle special routing
    if (menu === "SMM-content") {
      navigate("/portfoliu/smm-content");
    } else if (menu === "summerdeals") {
      navigate("/summerdeals");
    } else {
      const path = menu === "home" ? "/" : `/${menu}`;
      navigate(path);
    }
  };

  const handleSubItemClick = (subItem: string, e: React.MouseEvent) => {
    // Only allow clicks on active sub items
    if (!isSubItemActive(subItem)) {
      e.preventDefault();
      return;
    }
  };

  useEffect(() => {
    const currentPath = location.pathname.split("/")[1] || "home";
    setActiveMenu(currentPath);
  }, [location]);

  return (
    <>
      <div className={`menu-bar ${videoIsPlaying ? "hidden-header" : ""}`}>
        <div className="logo-container" onClick={() => navigate("/")} style={{ cursor: 'pointer' }}>
          <img className="logo" src={logo} alt="Logo" />
        </div>
        <div className="menu-items">
          {menuItems.map((menu) => {
            const displayName = menu.label || menu.name;
            return (
              <div
              onMouseEnter={() => menu.subItems && handleMouseEnter(menu.name)}
              onMouseLeave={() => menu.subItems && handleMouseLeave()}
              key={menu.name}
              className="menu-item-wrapper"
              style={{ 
                opacity: isNavItemActive(menu.name) ? 1 : 0.4, // Darker grey for disabled
                cursor: isNavItemActive(menu.name) ? 'pointer' : 'default',
                position: 'relative'
              }}
            >
              <div
                className={`menu-item ${
                  activeMenu === menu.name ? "active" : ""
                }`}
                onClick={() => !menu.subItems && handleMenuClick(menu.name)}
                style={{
                  cursor: isNavItemActive(menu.name) ? 'pointer' : 'default',
                  // No hover effects for disabled items
                  pointerEvents: isNavItemActive(menu.name) ? 'auto' : 'none'
                }}
              >
                <span className="menu-body" style={{ whiteSpace: 'nowrap' }}>{displayName}</span>
              </div>
              <AnimatePresence mode="wait">
                {menu.subItems && hoveredMenu === menu.name && (
                  <motion.div
                    key="dropdown"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="menu-dropdown"
                  >
                    {menu.subItems.map((sub, idx) => {
                      const href = isSubItemActive(sub) ? `/${menu.name}/${sub.replace(/\s+/g, "-")}` : "#";
                      return (
                        <a
                          href={href}
                          key={idx}
                          className="menu-dropdown-item title-4"
                          onClick={(e) => {
                            if (isSubItemActive(sub)) {
                              e.preventDefault();
                              navigate(href);
                            }
                            handleSubItemClick(sub, e);
                          }}
                          style={{ 
                            opacity: isSubItemActive(sub) ? 1 : 0.3, // Much darker for disabled
                            cursor: isSubItemActive(sub) ? 'pointer' : 'default',
                            pointerEvents: isSubItemActive(sub) ? 'auto' : 'none',
                            textDecoration: 'none'
                          }}
                        >
                          {sub}
                          <span>{isSubItemActive(sub) ? "Află mai multe" : "În curând"}</span>
                        </a>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            );
          })}
        </div>
        <div className="contact-container">
          <Button
            className="menu-button button-body"
            text="Contacteaza-ne"
            onClick={() => navigate("/contact")} // Always enabled, direct navigation
            
          />
        </div>
      </div>
      
      {/* BURGER BUTTON OUTSIDE HEADER - SEPARATE STACKING CONTEXT */}
      <button
        className="burger-trigger"
        onClick={() => {
          console.log('Burger clicked! Current state:', burgerOpen);
          setBurgerOpen((prev) => !prev);
        }}
        aria-label={burgerOpen ? "Close menu" : "Open menu"}
      >
        <div className={`burger-lines ${burgerOpen ? 'open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>
      
      <SideMenu
        onClose={() => setBurgerOpen(false)}
        isOpen={burgerOpen}
      />
    </>
  );
};

export default Header;
